import React from "react";
import { connect, useSelector } from "react-redux";
import { Grid } from "semantic-ui-react";
import { SidePanel, Form } from "@redriver/cinnamon";
import { FilterButton } from "features/../../../shared/components/buttons";
import { useTranslation } from "react-i18next";
import { UserTypeAhead } from "features/Settings";
import { UserArea, UserType } from "features/../../../shared/constants/enums";
import {
  PermissionCheck,
  withCurrentUser,
} from "features/../../../shared/components/auth";
import {
  Targets,
  Actions,
} from "features/../../../shared/constants/permissions";
import { getAgreementsState } from "../selectors";

import {
  DocuSignSheetStateDropdownLookup,
  PhysicalSheetStateDropdownLookup,
} from "features/Sheets";

export const FieldNames = {
  Search: "search",
  SearchType: "searchType",
  SheetStatesIps: "sheetStatesIps",
  SheetStatesPrincipal: "sheetStatesPrincipal",
  PhysicalDocumentSheetStateIps: "physicalDocumentSheetStateIps",
  PhysicalDocumentSheetStatePrincipal: "physicalDocumentSheetStatePrincipal",
  SheetOwnerId: "sheetOwnerId",
  SheetOwner: "sheetOwner",
  SheetOwnerName: "sheetOwnerName",
  IsCrystallised: "isCrystallised",
  IsUncrystallised: "isUncrystallised",
  IsArchived: "isArchived",
  IsExpired: "isExpired",
  NotExpired: "notExpired",
  NotArchived: "notArchived",
};

export const FieldNamesToTranslationKeys = {
  [FieldNames.SheetStatesIps]: "DocusignStates",
  [FieldNames.SheetStatesPrincipal]: "DocusignStates",
  [FieldNames.PhysicalDocumentSheetStateIps]: "PhysicalDocStates",
  [FieldNames.PhysicalDocumentSheetStatePrincipal]: "PhysicalDocStates",
  [FieldNames.SheetOwnerId]: "SheetOwner",
  [FieldNames.SheetOwnerName]: "SheetOwner",
  [FieldNames.IsCrystallised]: "IsCrystallised",
  [FieldNames.IsUncrystallised]: "IsUncrystallised",
  [FieldNames.IsArchived]: "IsArchived",
  [FieldNames.NotArchived]: "NotArchived",
  [FieldNames.IsExpired]: "IsExpired",
  [FieldNames.NotExpired]: "NotExpired",
};

const AgreementSheetFilters = ({ formProps, currentUser }) => {
  const { t } = useTranslation("agreements", { keyPrefix: "AgreementFilters" });
  const { filters } = useSelector(getAgreementsState);

  const dealerId = filters?.dealer?.id;
  let userIsDealer = currentUser.userType == UserType.Dealer;
  const isAccountManager =
    currentUser.userArea === UserArea.AccountManager && userIsDealer;

  return (
    <SidePanel
      closeIcon
      trigger={<FilterButton />}
      className="sheets-filter-side-panel"
    >
      <div className="ui form highlighted-inputs">
        <Form {...formProps}>
          <div className="ui header filters-title">
            <div className="title-text">{t("Title")}</div>
          </div>
          <Grid>
            <Grid.Column>
              <DocuSignSheetStateDropdownLookup
                field={
                  process.env.TRADE_AS_IPS
                    ? FieldNames.SheetStatesIps
                    : FieldNames.SheetStatesPrincipal
                }
                label={t(
                  FieldNamesToTranslationKeys[
                    process.env.TRADE_AS_IPS
                      ? FieldNames.SheetStatesIps
                      : FieldNames.SheetStatesPrincipal
                  ],
                )}
                clearable
                fluid
                multiple
              />
              <PhysicalSheetStateDropdownLookup
                field={
                  process.env.TRADE_AS_IPS
                    ? FieldNames.PhysicalDocumentSheetStateIps
                    : FieldNames.PhysicalDocumentSheetStatePrincipal
                }
                label={t("PhysicalDocStates")}
                clearable
                fluid
                multiple
              />
              {!isAccountManager && (
                <UserTypeAhead
                  field={FieldNames.SheetOwnerId}
                  label={t(
                    FieldNamesToTranslationKeys[FieldNames.SheetOwnerId],
                  )}
                  lookupParams={{
                    dealerId,
                  }}
                  placeholder={t("SheetOwner_Placeholder")}
                  clearable
                  fluid
                  textField="sheetOwnerName"
                  optionField="sheetOwner"
                  unknownValueOptions={[formProps.value.sheetOwner]}
                />
              )}

              <br />
              <Form.Checkbox
                field={FieldNames.IsCrystallised}
                label={t(
                  FieldNamesToTranslationKeys[FieldNames.IsCrystallised],
                )}
              />
              <Form.Checkbox
                field={FieldNames.IsUncrystallised}
                label={t(
                  FieldNamesToTranslationKeys[FieldNames.IsUncrystallised],
                )}
              />
              <Form.Checkbox
                field={FieldNames.IsArchived}
                label={t(FieldNamesToTranslationKeys[FieldNames.IsArchived])}
              />
              <Form.Checkbox
                field={FieldNames.NotArchived}
                label={t(FieldNamesToTranslationKeys[FieldNames.NotArchived])}
              />
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.ViewExpired}
              >
                <Form.Checkbox
                  field={FieldNames.IsExpired}
                  label={t(FieldNamesToTranslationKeys[FieldNames.IsExpired])}
                />
                <Form.Checkbox
                  field={FieldNames.NotExpired}
                  label={t(FieldNamesToTranslationKeys[FieldNames.NotExpired])}
                />
              </PermissionCheck>
            </Grid.Column>
          </Grid>
        </Form>
      </div>
    </SidePanel>
  );
};

const mapStateToProps = (state) => {
  const { filters } = getAgreementsState(state);
  return {
    dealerId: filters.dealer?.id,
  };
};

export default connect(
  mapStateToProps,
  null,
)(withCurrentUser(AgreementSheetFilters));
